import React from "react";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import {
  Box,
  BoxProps,
  Button,
  Card,
  CardBody,
  Collapse,
  HStack,
  Heading,
  Icon,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";

import { StepFrontend, getHumanReadableStepName } from "@constants";

type CommentViewProps = BoxProps & {
  steps: StepFrontend[];
};

/**
 * A re-useable component to display all the comments across a collection of
 * steps. Mainly used for Module and Component Pages.
 * @param {Step[]} steps The steps to collect coments out of.
 */
function CommentView({ steps, ...delegated }: CommentViewProps) {
  const { isOpen, onToggle } = useDisclosure();
  const comments = steps
    .map(({ stepName, comments, timestamp, userName }) => ({
      title: getHumanReadableStepName(stepName),
      comments,
      commenter: userName,
      key: `${stepName}---${timestamp}---${comments.slice(0, 10)}`,
    }))
    .filter(({ comments }) => !!comments);
  if (comments.length < 1) return null;
  return (
    <Box {...delegated}>
      <HStack alignItems="baseline">
        <Heading size="md">Comments</Heading>
        <Button variant="ghost" onClick={onToggle}>
          <Icon as={isOpen ? FaCaretUp : FaCaretDown} />
        </Button>
      </HStack>
      <Card as={Collapse} in={isOpen} animateOpacity>
        <VStack as={CardBody} w="100%" alignItems="stretch" gap="8px">
          {comments.map(({ title, comments, commenter, key }) => (
            <React.Fragment key={key}>
              <Heading as="h3" size="sm">
                {title} ({commenter})
              </Heading>
              <Text>{comments}</Text>
            </React.Fragment>
          ))}
        </VStack>
      </Card>
    </Box>
  );
}

export { CommentView };
